import { Search as SearchIcon } from "@mui/icons-material";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Typography,
  OutlinedInput,
  TextField,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { prescriptionApi } from "apis";
import { PrescriptionPatientDto } from "libs/cellera-sdk";
import { isNil } from "lodash";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface PatientSelectForPrescriptionProps {
  onChange: (value: PrescriptionPatientDto | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export default function PatientSelectForPrescription({
  onChange,
  placeholder = "Select Patient",
  label = "Select Patient",
  disabled = false,
  error = false,
  helperText,
}: PatientSelectForPrescriptionProps) {
  const { colors } = useCelleraTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedPatient, setSelectedPatient] =
    useState<PrescriptionPatientDto | null>(null);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [accumulatedPatients, setAccumulatedPatients] = useState<
    PrescriptionPatientDto[]
  >([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
      setAccumulatedPatients([]);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: patientsData,
    isLoading,
    error: apiError,
  } = useQuery({
    queryKey: ["prescriptionPatients", debouncedSearch, currentPage],
    queryFn: async () => {
      const response = await prescriptionApi.providerGetPatients({
        search: debouncedSearch || undefined,
        page: currentPage,
        pageSize: 50,
      });
      return response;
    },
    retry: 2,
  });

  // Update accumulated patients when new data arrives
  useEffect(() => {
    if (patientsData?.items) {
      const filteredItems = patientsData.items.filter(
        (item) => !isNil(item?.patientId)
      );
      if (currentPage === 1) {
        setAccumulatedPatients(filteredItems);
      } else {
        setAccumulatedPatients((prev) => [...prev, ...filteredItems]);
        setIsLoadingMore(false);
      }
    }
  }, [patientsData, currentPage]);

  const patients = useMemo((): PrescriptionPatientDto[] => {
    return accumulatedPatients;
  }, [accumulatedPatients]);

  const hasMore = patientsData?.pagination?.hasMore ?? false;

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedValue = event.target.value as string;

      if (!selectedValue) {
        setSelectedPatient(null);
        onChange(null);
        return;
      }

      const patient = patients.find(
        (option) => option.patientId === selectedValue
      );
      if (patient?.patientId === selectedPatient?.patientId) {
        setSelectedPatient(null);
        onChange(null);
        return;
      }

      setSelectedPatient(patient || null);
      onChange(patient || null);

      if (patient) {
        setSearchTerm("");
      }
    },
    [patients, selectedPatient, onChange]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  }, []);

  const handleClose = useCallback(
    (event: React.SyntheticEvent) => {
      // Don't close if clicking on "Load More"
      const target = event.target as HTMLElement;
      if (target.closest('[data-load-more="true"]')) {
        return; // Keep menu open
      }
      setOpen(false);
      if (!selectedPatient) {
        setSearchTerm("");
      }
    },
    [selectedPatient]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      if (selectedPatient) {
        setSelectedPatient(null);
        onChange(null);
      }
    },
    [selectedPatient, onChange]
  );

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoadingMore && !isLoading) {
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore, isLoadingMore, isLoading]);

  const contentDetails = useMemo(() => {
    if (isLoading) {
      return (
        <MenuItem disabled>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
          </Box>
        </MenuItem>
      );
    }
    if (apiError) {
      return (
        <MenuItem disabled>
          <Typography color="error">Error loading patients</Typography>
        </MenuItem>
      );
    }
    if (patients.length === 0) {
      return (
        <MenuItem disabled>
          <Typography>No patients found</Typography>
        </MenuItem>
      );
    }
    return [
      ...patients.map((patient) => (
        <MenuItem key={patient.patientId} value={patient.patientId}>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {patient.firstName} {patient.lastName}
            </Typography>
            {patient.email && (
              <Typography
                variant="caption"
                sx={{ color: colors.text.secondary }}
              >
                {patient.email}
              </Typography>
            )}
          </Box>
        </MenuItem>
      )),
      ...(hasMore
        ? [
            <MenuItem
              key="__load_more__"
              data-load-more="true"
              onClick={handleLoadMore}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled={isLoadingMore}
              sx={{
                justifyContent: "center",
                py: 1.5,
                cursor: isLoadingMore ? "default" : "pointer",
                "&:hover": {
                  backgroundColor: isLoadingMore
                    ? "transparent"
                    : colors.background.secondary,
                },
                "&.Mui-disabled": {
                  opacity: 1,
                },
              }}
            >
              {isLoadingMore ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary }}
                  >
                    Loading...
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  Load More
                </Typography>
              )}
            </MenuItem>,
          ]
        : []),
    ];
  }, [
    apiError,
    isLoading,
    patients,
    colors.text.secondary,
    colors.background.secondary,
    colors.primary.main,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  ]);

  return (
    <FormControl error={error || !!apiError} fullWidth>
      <InputLabel id="patient-select-label">{label}</InputLabel>
      <Select
        labelId="patient-select-label"
        value={selectedPatient?.patientId || ""}
        onChange={handleChange}
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          if (!selected) return "";
          const patient: PrescriptionPatientDto | undefined = patients.find(
            (p) => p.patientId === selected
          );
          if (!patient || !patient.email) return "";
          const fullName =
            [patient.firstName, patient.lastName].filter(Boolean).join(" ") ??
            undefined;
          return fullName
            ? `${patient.firstName} ${patient.lastName} (${patient.email})`
            : patient.email;
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 700,
              borderRadius: 0,
              width: 500,
              minWidth: "unset",
              maxWidth: "unset",
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.border.light,
            },
            "&:hover fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.border.main,
            },
            "&.Mui-focused fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.secondary.main,
            },
          },
          width: "100%",
        }}
      >
        <MenuItem>
          <TextField
            ref={searchInputRef}
            fullWidth
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            variant="outlined"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="text-gray-400" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                "& fieldset": {
                  borderColor: colors.border.light,
                },
                "&:hover fieldset": {
                  borderColor: colors.border.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.secondary.main,
                },
              },
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </MenuItem>
        {contentDetails}
      </Select>
      {helperText && (
        <Typography
          variant="caption"
          color={error || apiError ? "error" : "text.secondary"}
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText || (apiError ? "Failed to load patients" : undefined)}
        </Typography>
      )}
    </FormControl>
  );
}
